package com.htmlagent.site.components.sections.landing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.htmlagent.shared.UiLocale
import com.htmlagent.site.components.sections.Glyph
import com.htmlagent.site.components.widgets.LocaleCode
import com.htmlagent.site.components.widgets.Mark
import com.htmlagent.site.i18n.AppLocale
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.theme.Icons
import com.htmlagent.site.theme.LandingInnerStyle
import com.htmlagent.site.theme.LandingMarkNameStyle
import com.htmlagent.site.theme.LandingMarkTaglineStyle
import com.htmlagent.site.theme.LandingMarkTextStyle
import com.htmlagent.site.theme.LandingMarkStyle
import com.htmlagent.site.theme.LandingMarkTileStyle
import com.htmlagent.site.theme.LandingNavActionsStyle
import com.htmlagent.site.theme.LandingNavLinkStyle
import com.htmlagent.site.theme.LandingNavLinksStyle
import com.htmlagent.site.theme.LandingNavRowStyle
import com.htmlagent.site.theme.LandingNavStyle
import com.htmlagent.site.theme.NavMenuButtonStyle
import com.htmlagent.site.theme.NavSheetLinksStyle
import com.htmlagent.site.theme.NavSheetStyle
import com.htmlagent.site.theme.NavSheetSwitchesStyle
import com.htmlagent.site.theme.NavSwitchVariant
import com.htmlagent.site.theme.NavSwitchesStyle
import com.htmlagent.site.theme.PillPrimaryVariant
import com.htmlagent.site.theme.labelled
import com.htmlagent.site.theme.navPill
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.Node
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

/** How far the page has to move before the bar stops being transparent. */
private const val SOLID_AFTER_PX = 8

/**
 * The bar at the top: the mark, the section anchors, the two switches and the one call to action.
 *
 * It is transparent over the hero and turns to glass as soon as the page moves under it, which is
 * the only way a bar can sit on a headline without cutting it in half. The state is a data
 * attribute rather than a second class so it wins on specificity no matter what order Kobweb
 * registers the styles in.
 *
 * Below a desktop the anchors and the switches move into a menu behind one button rather than
 * disappearing: without it a phone had no navigation at all, and the only copies of those links
 * were in the footer of a page nine thousand pixels long.
 *
 * What the bar does NOT carry is anything that is not a place or a door. The colour mode lives
 * at the foot of the page and "Install app" floats over it, because a row with eleven controls
 * in it reads as clutter however well each one is measured: eleven left 68 px of air in a
 * measure of 1116, and the three that went were the three nobody presses twice.
 *
 * There is one door. Starting and coming back used to be two, but both now open the same entry
 * dialog, which asks for an email or lets you go on as a guest, so a second button only repeated
 * it. [known] is whether this browser is signed in: then the pill says "Continuar" and goes
 * straight to the workspace.
 */
@Composable
fun LandingNav(known: Boolean, onStart: () -> Unit) {
    val t = LocalStrings.current
    var scrolled by remember { mutableStateOf(false) }
    var menuOpen by remember { mutableStateOf(false) }
    var bar by remember { mutableStateOf<HTMLElement?>(null) }

    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = { scrolled = window.scrollY > SOLID_AFTER_PX }
        // Read once on mount: the page can already be scrolled after a reload.
        scrolled = window.scrollY > SOLID_AFTER_PX
        window.addEventListener("scroll", listener)
        onDispose { window.removeEventListener("scroll", listener) }
    }

    DisposableEffect(menuOpen) {
        val onKey: (Event) -> Unit = { event ->
            if ((event as? KeyboardEvent)?.key == "Escape") menuOpen = false
        }
        if (menuOpen) document.addEventListener("keydown", onKey)
        onDispose { document.removeEventListener("keydown", onKey) }
    }

    // A touch anywhere else closes it. Without this the menu stayed over the page on a phone,
    // where there is no Escape and nothing else to press but the page it is covering.
    DisposableEffect(menuOpen) {
        val outside: (Event) -> Unit = { event ->
            val inside = (event.target as? Node)?.let { bar?.contains(it) } == true
            if (!inside) menuOpen = false
        }
        if (menuOpen) document.addEventListener("pointerdown", outside)
        onDispose { document.removeEventListener("pointerdown", outside) }
    }

    Header(
        LandingNavStyle.toModifier().toAttrs {
            attr("data-scrolled", scrolled.toString())
            ref { element -> bar = element as? HTMLElement; onDispose { bar = null } }
        },
    ) {
        Div(LandingInnerStyle.toModifier().then(LandingNavRowStyle.toModifier()).toAttrs()) {
            Wordmark()

            Nav(LandingNavLinksStyle.toModifier().toAttrs { attr("aria-label", t.landing.navMenu) }) {
                SectionLinks()
            }

            Div(LandingNavActionsStyle.toModifier().toAttrs()) {
                LanguageSwitch(NavSwitchesStyle.toModifier())
                Button(
                    onClick = { onStart() },
                    modifier = Modifier.navPill(),
                    variant = PillPrimaryVariant,
                ) {
                    Text(if (known) t.landing.navContinue else t.landing.navStart)
                }
                Button(
                    onClick = { menuOpen = !menuOpen },
                    modifier = NavMenuButtonStyle.toModifier()
                        .labelled(if (menuOpen) t.landing.navCloseMenu else t.landing.navOpenMenu)
                        .attrsModifier { attr("aria-expanded", menuOpen.toString()) },
                    variant = null,
                ) {
                    Glyph(if (menuOpen) Icons.Close else Icons.Menu)
                }
            }
        }

        // `inert` and not `aria-hidden`: the closed menu kept every one of its controls in the
        // tab order, which is the bug this project has already paid for once in the workspace.
        Div(
            NavSheetStyle.toModifier().toAttrs {
                attr("data-open", menuOpen.toString())
                if (!menuOpen) attr("inert", "")
            },
        ) {
            Nav(
                NavSheetLinksStyle.toModifier()
                    .onClick { menuOpen = false }
                    .toAttrs { attr("aria-label", t.landing.navMenu) },
            ) {
                SectionLinks()
            }
            LanguageSwitch(NavSheetSwitchesStyle.toModifier())
        }
    }
}

/**
 * The five anchors, written once and painted twice: in the bar on a desktop and in the sheet on
 * a phone. Examples was missing from both, which left the most convincing band on the page
 * reachable only by scrolling past it.
 */
@Composable
private fun SectionLinks() {
    val t = LocalStrings.current
    NavLink(SECTION_STEPS, t.landing.navHowItWorks)
    NavLink(SECTION_STATS, t.landing.navInside)
    NavLink(SECTION_AUDIENCE, t.landing.navAudience)
    NavLink(SECTION_SAMPLES, t.landing.navSamples)
    NavLink(SECTION_FAQ, t.landing.navQuestions)
}

/** The product mark and its name, which is also the way back to the top of the page. */
@Composable
private fun Wordmark() {
    A(href = "#$SECTION_HERO", attrs = LandingMarkStyle.toModifier().toAttrs()) {
        Span(LandingMarkTileStyle.toModifier().toAttrs()) { Mark() }
        Span(LandingMarkTextStyle.toModifier().toAttrs()) {
            Span(LandingMarkNameStyle.toModifier().toAttrs()) { Text(PRODUCT_NAME) }
            Span(LandingMarkTaglineStyle.toModifier().toAttrs()) { Text(PRODUCT_TAGLINE) }
        }
    }
}

/** One anchor in the bar. */
@Composable
private fun NavLink(section: String, label: String) {
    A(href = "#$section", attrs = LandingNavLinkStyle.toModifier().toAttrs()) { Text(label) }
}

/**
 * The language, and only the language.
 *
 * It is here and not only inside the application because somebody deciding whether to use this
 * has to be able to read it: an English speaker facing a Spanish landing has already left. The
 * colour mode used to stand beside it and now lives at the foot of the page - it is a setting
 * somebody changes once, and the bar was the most expensive place on the site to keep it.
 */
@Composable
private fun LanguageSwitch(modifier: Modifier) {
    val t = LocalStrings.current

    Div(modifier.toAttrs()) {
        val locale by AppLocale.current.collectAsState()
        val other = if (locale == UiLocale.SPANISH) UiLocale.ENGLISH else UiLocale.SPANISH
        val label = if (other == UiLocale.ENGLISH) t.rail.switchToEnglish else t.rail.switchToSpanish

        Button(
            onClick = { AppLocale.set(other) },
            modifier = Modifier.labelled(label),
            variant = NavSwitchVariant,
        ) {
            LocaleCode(other)
        }
    }
}
